import React from 'react';
import { useDispatch } from 'react-redux';
import dayjs, { Dayjs } from 'dayjs';

import { Box, MenuItem, Select, SelectChangeEvent, Typography, useTheme } from '@mui/material';

import DatePicker from 'components/DatePicker';

import { periodSelectorActions } from './periodSelectorSlice';

export interface PeriodSelectorProps {
  /** Current selected date filter */
  selectedFilter: string;
  /** Current from date */
  fromDate: Dayjs | null;
  /** Current to date */
  toDate: Dayjs | null;
  /** Available filter options */
  filterOptions?: string[];
  /** Callback when filter selection changes */
  onFilterChange: (filter: string) => void;
  /** Callback when from date changes */
  onFromDateChange: (date: Dayjs | null) => void;
  /** Callback when to date changes */
  onToDateChange: (date: Dayjs | null) => void;
  /** Label for the view selector */
  viewLabel?: string;
  /** Label for from date picker */
  fromDateLabel?: string;
  /** Label for to date picker */
  toDateLabel?: string;
  /** Custom date option name (used to enable/disable date pickers) */
  customDateOption?: string;
}

const defaultFilterOptions = ['Daily', 'Past Week', 'Past Month', 'Custom Date'];

const PeriodSelector: React.FC<PeriodSelectorProps> = ({
  selectedFilter,
  fromDate,
  toDate,
  filterOptions = defaultFilterOptions,
  onFilterChange,
  onFromDateChange,
  onToDateChange,
  viewLabel = 'View',
  fromDateLabel = 'FROM',
  toDateLabel = 'TO',
  customDateOption = 'Custom Date',
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const handleFromDateChange = async (date: Dayjs | null) => {
    onFromDateChange(date);
    await dispatch(periodSelectorActions.setFromDate(date ? date.format('YYYY-MM-DD') : null));
  };

  const handleToDateChange = async (date: Dayjs | null) => {
    onToDateChange(date);
    await dispatch(periodSelectorActions.setToDate(date ? date.format('YYYY-MM-DD') : null));
  };

  const handleDateFilterChange = async (event: SelectChangeEvent) => {
    const selectedFilterValue = event.target.value as string;
    let newFromDate: Dayjs | null = fromDate;
    let newToDate: Dayjs | null = toDate;

    // Auto-calculate dates for predefined filters
    if (filterOptions.indexOf(selectedFilterValue) === 1) {
      // Past Week
      newFromDate = dayjs().subtract(1, 'week');
      newToDate = dayjs();
      await dispatch(
        periodSelectorActions.setPeriodSelectorState({
          selectedFilterValue: selectedFilterValue,
          fromDate: newFromDate.format('YYYY-MM-DD'),
          toDate: newToDate.format('YYYY-MM-DD'),
        }),
      );
      onFromDateChange(newFromDate);
      onToDateChange(newToDate);
    } else if (filterOptions.indexOf(selectedFilterValue) === 2) {
      // Past Month
      newFromDate = dayjs().subtract(1, 'month');
      newToDate = dayjs();
      await dispatch(
        periodSelectorActions.setPeriodSelectorState({
          selectedFilterValue: selectedFilterValue,
          fromDate: newFromDate.format('YYYY-MM-DD'),
          toDate: newToDate.format('YYYY-MM-DD'),
        }),
      );
      onFromDateChange(newFromDate);
      onToDateChange(newToDate);
    } else if (filterOptions.indexOf(selectedFilterValue) === 0) {
      // Daily
      newFromDate = dayjs().subtract(1, 'day');
      newToDate = dayjs();
      await dispatch(
        periodSelectorActions.setPeriodSelectorState({
          selectedFilterValue: selectedFilterValue,
          fromDate: newFromDate.format('YYYY-MM-DD'),
          toDate: newToDate.format('YYYY-MM-DD'),
        }),
      );
      onFromDateChange(newFromDate);
      onToDateChange(newToDate);
    }

    onFilterChange(selectedFilterValue);
    await dispatch(periodSelectorActions.setSelectedFilterValue(selectedFilterValue));
  };

  return (
    <>
      <Typography variant="h6" gutterBottom>
        {viewLabel}
      </Typography>

      <Select
        size="small"
        value={selectedFilter}
        onChange={handleDateFilterChange}
        sx={{
          width: '100%',
        }}>
        {filterOptions.map((filter) => {
          return (
            <MenuItem key={filter} value={filter}>
              {filter}
            </MenuItem>
          );
        })}
      </Select>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          mt: theme.spacing(2),
        }}>
        <Typography variant="body2" gutterBottom>
          {fromDateLabel}
        </Typography>
        <DatePicker onChange={handleFromDateChange} value={fromDate} disabled={selectedFilter !== customDateOption} />
        <Typography variant="body2" gutterBottom mt={1}>
          {toDateLabel}
        </Typography>
        <DatePicker onChange={handleToDateChange} value={toDate} disabled={selectedFilter !== customDateOption} />
      </Box>
    </>
  );
};

export default PeriodSelector;
